/**
 * Sent-messages history section, rendered into the account drawer's history
 * tab. Replaces `message-history.js::renderMessageHistorySectionInto`.
 */

import { createLogger } from '../../core/logger.js';
import { escape } from '../../shared/escape.js';
import { format as formatPlate } from '../../shared/plate.js';
import { timeAgo } from '../../shared/date.js';
import * as messageService from './message.service.js';
import * as alert from '../../core/alert.js';

const log = createLogger('history.view');

/**
 * Render into the given host element (by ID).
 * @param {string} hostId
 */
export async function renderInto(hostId) {
  const host = document.getElementById(hostId);
  if (!host) {
    log.warn(`host #${hostId} not found`);
    return;
  }
  host.innerHTML = '<div class="msg-history-empty">טוען היסטוריה...</div>';

  /** @type {any[]} */
  let rows = [];
  try {
    rows = await messageService.listSent();
  } catch (err) {
    log.warn('listSent failed:', err);
    host.innerHTML = '<div class="msg-history-empty">שגיאה בטעינת ההיסטוריה.</div>';
    return;
  }

  if (rows.length === 0) {
    host.innerHTML = '<div class="msg-history-empty">לא שלחת הודעות עדיין.</div>';
    return;
  }

  host.innerHTML = `
    <div class="msg-history-list">${rows.map(rowHtml).join('')}</div>
    <button class="msg-history-clear" type="button">מחק את כל ההיסטוריה</button>
  `;

  host.querySelectorAll('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const msgId = btn.getAttribute('data-msg-id') ?? '';
      const convId = btn.getAttribute('data-conv-id') ?? '';
      const ok = await alert.showConfirm({
        title: 'מחיקת הודעה',
        message: 'למחוק את ההודעה הזו?',
        danger: true,
      });
      if (!ok) return;
      await messageService.deleteSent({ msgId, convId });
      await renderInto(hostId);
    });
  });

  host.querySelector('.msg-history-clear')?.addEventListener('click', async () => {
    const ok = await alert.showConfirm({
      title: 'מחיקת היסטוריה',
      message: 'למחוק את כל ההודעות ששלחת? הפעולה אינה הפיכה.',
      danger: true,
    });
    if (!ok) return;
    await messageService.deleteAllSent();
    await renderInto(hostId);
  });

  injectStyles();
}

/** @param {any} row */
function rowHtml(row) {
  const plate = row.toPlate ? formatPlate(row.toPlate) : '—';
  return `
    <div class="msg-history-item">
      <div class="msg-history-row1">
        <span class="msg-history-plate" dir="ltr">${escape(plate)}</span>
        <span class="msg-history-time">${escape(timeAgo(row.sentAt || 0))}</span>
      </div>
      <div class="msg-history-body">${escape(row.body || '')}</div>
      <div class="msg-history-actions">
        <button class="msg-history-del" type="button"
          data-action="delete"
          data-msg-id="${escape(row.id)}"
          data-conv-id="${escape(row.convId || '')}">מחק</button>
        ${row.smsSent ? '<span class="msg-history-sms">📱 SMS נשלח</span>' : ''}
      </div>
    </div>
  `;
}

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .msg-history-empty { text-align: center; color: #6b7a89; padding: 30px 10px; }
    .msg-history-list { display: flex; flex-direction: column; gap: 8px; }
    .msg-history-item {
      background: #fff; border: 1px solid #e2e8f0; border-radius: 10px;
      padding: 10px 12px;
    }
    .msg-history-row1 { display: flex; justify-content: space-between; margin-bottom: 4px; }
    .msg-history-plate { font-weight: 700; }
    .msg-history-time { font-size: 12px; color: #6b7a89; }
    .msg-history-body { font-size: 14px; color: #2c3e50; white-space: pre-wrap; }
    .msg-history-actions { display: flex; gap: 10px; align-items: center; margin-top: 6px; }
    .msg-history-del {
      background: transparent; color: #a94442; border: 0; cursor: pointer;
      font-weight: 600; padding: 4px 0;
    }
    .msg-history-sms { font-size: 12px; color: #1e7e34; margin-right: auto; }
    .msg-history-clear {
      width: 100%; margin-top: 12px; padding: 10px;
      background: #fdecea; color: #a94442; border: 1px solid #f5c6cb;
      border-radius: 10px; font-weight: 600; cursor: pointer;
    }
  `;
  document.head.appendChild(style);
}
